"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioLines,
  AudioWaveform,
  CircleGauge,
  Coins,
  FolderOpen,
  Home,
  LogOut,
  Mail,
  Menu,
  Mic,
  ShieldCheck,
  Sparkles,
  User,
  X,
} from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { DESKTOP_BUILD } from "@/lib/desktop";

// `note` is the small status tag drawn beside a label. It says where a
// product stands, nothing more: the dubbing studio is not open yet, Neo
// Noise is free to try while it is in its first stage.
const NAV_ITEMS: { icon: typeof Home; label: string; href: string; note?: string }[] = [
  { icon: Home, label: "Home", href: "/" },
  // Every product has its own entry here, whichever one happens to hold the
  // front page at the moment (the plugins, today) — so these two point INTO
  // that page rather than at pages of their own.
  { icon: AudioWaveform, label: "Warper", href: "/#warper" },
  { icon: CircleGauge, label: "Neo Pitch One", href: "/#neo-pitch-one" },
  { icon: Mic, label: "Neo Dub Studio", href: "/neo-dub-studio", note: "Coming soon" },
  { icon: Sparkles, label: "Neo Noise", href: "/neo-noise", note: "Live test" },
  { icon: Mail, label: "Contact", href: "/contact" },
];

// The desktop app opens straight on the product (brief §0: the marketing
// site does not come across) — but the export still bundles every page
// under app/, so a stray link to "/" or "/pricing" would actually load in
// the shell rather than 404. This is the one place those links are drawn,
// so it is the one place that needs to know: everything except the studio
// itself and Neo Noise (an actual owner-used tool, not a sales page) is
// filtered out of the menu here rather than out of the export, keeping
// this a config value, not a fork.
const DESKTOP_NAV_ITEMS = NAV_ITEMS.filter((item) => item.label === "Neo Noise");

export function SiteNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Landing page is public — this must NOT redirect on a 401 like
  // useRequireAuth does, it only needs to know whether to greet a real
  // account or a guest.
  const meQuery = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const loggedIn = meQuery.isSuccess;

  const queryClient = useQueryClient();
  const logoutMutation = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setMenuOpen(false);
    },
  });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    // Sticky: the menu has to be reachable from anywhere on a long page, not
    // only after scrolling back to the top (owner, 2026-09-19).
    <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-sidebar px-4 py-3 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex size-8 items-center justify-center rounded-lg border border-border/60 bg-secondary/60 text-muted-foreground transition-colors hover:text-foreground"
          aria-label={t("Navigation menu")}
        >
          <Menu className="size-4" />
        </button>

        {menuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          />
        )}

        <div
          ref={menuRef}
          className={cn(
            "fixed left-0 top-0 z-50 flex h-full w-72 flex-col border-r border-border bg-card shadow-2xl transition-transform duration-300 ease-in-out",
            menuOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <AudioLines className="size-4" />
              </div>
              <span className="text-sm font-semibold">{t("Neo Dub Studio")}</span>
            </div>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t("Close menu")}
            >
              <X className="size-5" />
            </button>
          </div>

          <nav className="flex flex-col gap-1 p-3 flex-1">
            {!DESKTOP_BUILD && (
              <Link
                href="/"
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Home className="size-5 shrink-0" />
                {t("Home")}
              </Link>
            )}
            {/* Was ONE slot that swapped label+destination on login state
                ("Account" -> /login when signed out, "My projects" ->
                /projects when signed in) — never actually linked to the real
                /account page. Now two separate, real items: Projects only
                makes sense once there ARE projects (signed-in), Account
                links to the real settings page (2026-07-26). Signed-out
                visitors get a single "Sign in" item instead, matching the
                header's own CTA. */}
            {loggedIn ? (
              <>
                <Link
                  href="/projects"
                  onClick={() => setMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <FolderOpen className="size-5 shrink-0" />
                  {t("My projects")}
                </Link>
                <Link
                  href="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <User className="size-5 shrink-0" />
                  {t("Account")}
                </Link>
                {meQuery.data.is_admin && (
                  <Link
                    href="/golova"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <ShieldCheck className="size-5 shrink-0" />
                    {t("Admin")}
                  </Link>
                )}
              </>
            ) : (
              <Link
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <User className="size-5 shrink-0" />
                {t("Sign in")}
              </Link>
            )}
            {(DESKTOP_BUILD ? DESKTOP_NAV_ITEMS : NAV_ITEMS.slice(1)).map(({ icon: Icon, label, href, note }) => (
              <Link
                key={label}
                href={href}
                onClick={() => setMenuOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Icon className="size-5 shrink-0" />
                {t(label)}
                {note && (
                  <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {t(note)}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="flex flex-col gap-3 border-t border-border p-4">
            <div className="flex items-center gap-3 rounded-lg bg-secondary/50 px-4 py-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-primary/20 text-primary">
                <User className="size-4" />
              </div>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <span className="truncate">
                    {loggedIn ? meQuery.data.name || meQuery.data.email : "Guest"}
                  </span>
                  {loggedIn && meQuery.data.is_admin && (
                    <span className="shrink-0 rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      {t("Admin")}
                    </span>
                  )}
                </p>
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  {loggedIn ? (
                    <>
                      <Coins className="size-3" /> {meQuery.data.credits} credits
                    </>
                  ) : (
                    "Free plan"
                  )}
                </p>
              </div>
            </div>

            {loggedIn && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                <LogOut /> {t("Sign out")}
              </Button>
            )}
          </div>
        </div>

        {/* On the web this is the marketing homepage; the desktop app has
            none, so the brand mark takes you to the actual home of the
            app — My projects — instead of a dead link into the exported-
            but-unreachable-by-design landing page. */}
        <Link href={DESKTOP_BUILD ? "/projects" : "/"} className="flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <AudioLines className="size-4" />
          </div>
          <span className="text-sm font-semibold">{t("Neo Dub Studio")}</span>
        </Link>
      </div>

      {/* Links styled as buttons — nesting <Button> inside <Link> breaks tap
          navigation on mobile browsers (button swallows the tap).
          Sign up sits here, beside sign-in, where a visitor who has just
          read the landing page is deciding. It is deliberately NOT repeated
          in the side menu: that menu is for getting around, and a second
          copy of the same call to action only adds noise. */}
      <div className="flex items-center gap-1">
        {!loggedIn && (
          <Link
            href="/register"
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            Sign up
          </Link>
        )}
        <Link
          href={loggedIn ? "/projects" : "/login"}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
        >
          {loggedIn ? t("My projects") : t("Sign in")}
        </Link>
      </div>
    </header>
  );
}
